import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Copy, Check, Download, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import OtpInput from '../components/OtpInput';
import './Auth.css';
import './MFASetup.css';

const MFASetup: React.FC = () => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [manualEntryKey, setManualEntryKey] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [codesCopied, setCodesCopied] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setToken, logout } = useAuth();

  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates if component unmounts
    let hasRun = false; // Prevent double execution in React StrictMode

    // Try to get setupToken from location state first
    let setupToken = location.state?.setupToken;
    console.log('MFASetup: Token from location.state:', setupToken ? setupToken.substring(0, 30) + '...' : 'NOT FOUND');

    // If not in state, try to get from sessionStorage (in case of page refresh)
    if (!setupToken) {
      setupToken = sessionStorage.getItem('mfaSetupToken');
      console.log('MFASetup: Token from sessionStorage:', setupToken ? setupToken.substring(0, 30) + '...' : 'NOT FOUND');
    }

    if (!setupToken) {
      console.error('MFASetup: No token found in state or sessionStorage');
      if (isMounted) {
        toast.error('Invalid setup token. Please login again.');
        setTimeout(() => navigate('/login'), 2000);
      }
      return;
    }

    // Store token in sessionStorage for page refresh
    sessionStorage.setItem('mfaSetupToken', setupToken);
    console.log('MFASetup: Token stored in sessionStorage, length:', setupToken.length);

    // Check if we already have QR code data (prevent double call)
    if (qrCodeUrl && manualEntryKey) {
      console.log('MFASetup: QR code already generated, skipping setup request');
      setLoading(false);
      return;
    }

    // Generate MFA setup
    const generateSetup = async () => {
      // Prevent double execution
      if (hasRun) {
        console.log('MFASetup: Setup already running, skipping...');
        return;
      }
      hasRun = true;

      try {
        if (!setupToken) {
          throw new Error('Setup token is missing');
        }

        console.log('Sending MFA setup request with token:', setupToken.substring(0, 30) + '...');
        console.log('Token length:', setupToken.length);

        const response = await authApi.post('/auth/mfa/setup', { setupToken });

        console.log('MFA setup response received:', response.data ? 'Success' : 'No data');

        if (isMounted && response.data && response.data.qrCodeUrl && response.data.manualEntryKey) {
          setQrCodeUrl(response.data.qrCodeUrl);
          setManualEntryKey(response.data.manualEntryKey);
          setLoading(false);
        } else {
          console.error('Invalid response structure:', response.data);
          throw new Error('Invalid response from server');
        }
      } catch (error: any) {
        console.error('MFA setup error:', error);
        console.error('Error response:', error.response?.data);
        const errorMsg = error.response?.data?.error || error.message || 'Failed to generate MFA setup';
        if (isMounted) {
          toast.error(errorMsg);
          sessionStorage.removeItem('mfaSetupToken');
          setTimeout(() => navigate('/login'), 2000);
        }
        hasRun = false; // Reset flag on error
      }
    };

    generateSetup();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [location, navigate]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);

    try {
      // Get setupToken from state or sessionStorage
      let setupToken = location.state?.setupToken || sessionStorage.getItem('mfaSetupToken');

      console.log('MFA Verify: Getting setup token...');
      console.log('Token from location.state:', location.state?.setupToken ? 'Found' : 'Not found');
      console.log('Token from sessionStorage:', sessionStorage.getItem('mfaSetupToken') ? 'Found' : 'Not found');

      if (!setupToken) {
        console.error('MFA Verify: No setup token found');
        toast.error('Setup token not found. Please login again.');
        sessionStorage.removeItem('mfaSetupToken');
        navigate('/login');
        return;
      }

      console.log('MFA Verify: Sending verification request with token:', setupToken.substring(0, 30) + '...');

      const response = await authApi.post('/auth/mfa/verify-setup', {
        code,
        secret: manualEntryKey,
        setupToken,
      });

      const { user, token, backupCodes: codes } = response.data;

      // Store user and token in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Update AuthContext state
      setUser(user);
      setToken(token);

      // Set authorization header
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Clear setup token from sessionStorage
      sessionStorage.removeItem('mfaSetupToken');

      // Reset failed attempts on success
      setFailedAttempts(0);

      // Show backup codes
      setBackupCodes(codes);
      console.log('MFA setup successful, user logged in:', user.email);

      // Small delay to ensure state is updated
      setTimeout(() => {
        toast.success('MFA has been enabled successfully!');
      }, 100);
    } catch (error: any) {
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);

      // If 3 failed attempts, logout and redirect to login
      if (newFailedAttempts >= 3) {
        toast.error('Too many failed MFA setup attempts. Please login again.');
        // Clear all tokens and session data
        sessionStorage.removeItem('mfaSetupToken');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Logout
        logout();
        // Redirect to login
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
        return;
      }

      const remainingAttempts = 3 - newFailedAttempts;
      toast.error(
        error.response?.data?.error || `Invalid MFA code. ${remainingAttempts} attempt(s) remaining.`
      );
      setVerifying(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCodesCopied(true);
    toast.success('Backup codes copied to clipboard!');
    setTimeout(() => setCodesCopied(false), 2000);
  };

  const downloadBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mfa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Backup codes downloaded!');
  };

  const handleContinue = () => {
    navigate('/');
  };

  if (backupCodes.length > 0) {
    return (
      <div className="auth-shell auth-shell--center">
        <div className="auth-card auth-card--wide">
          <div className="auth-header">
            <div className="auth-icon-badge" style={{ background: 'var(--gradient-primary)' }}>
              <ShieldCheck size={28} />
            </div>
            <h1>Save Your Backup Codes</h1>
            <p>Keep these codes in a safe place. You can use them to access your account if you lose your authenticator device.</p>
          </div>
          <div className="backup-codes-container">
            <div className="backup-codes">
              {backupCodes.map((code, index) => (
                <div key={index} className="backup-code-item">
                  {code}
                </div>
              ))}
            </div>
            <div className="backup-codes-actions">
              <button onClick={copyBackupCodes} className="btn-secondary">
                {codesCopied ? <Check size={16} /> : <Copy size={16} />}
                {codesCopied ? 'Copied!' : 'Copy Codes'}
              </button>
              <button onClick={downloadBackupCodes} className="btn-secondary">
                <Download size={16} />
                Download
              </button>
            </div>
          </div>
          <button onClick={handleContinue} className="btn-primary auth-submit">
            Continue to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="auth-shell auth-shell--center">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-icon-badge">
              <Shield size={28} />
            </div>
            <h1>Setting Up MFA</h1>
          </div>
          <div className="loading-state">
            <div className="spinner spinner-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell auth-shell--center">
      <div className="auth-card auth-card--wide">
        <div className="auth-header">
          <div className="auth-icon-badge">
            <Shield size={28} />
          </div>
          <h1>Set Up Multi-Factor Authentication</h1>
          <p>Scan the QR code with your authenticator app to get started</p>
        </div>

        <div className="mfa-setup-content">
          <div className="qr-code-container">
            <img src={qrCodeUrl} alt="MFA QR Code" className="qr-code" />
          </div>

          <div className="manual-entry">
            <p className="manual-entry-label">Can't scan? Enter this code manually:</p>
            <div className="manual-entry-key">
              <code className="manual-code-text">{manualEntryKey}</code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(manualEntryKey);
                  toast.success('Code copied to clipboard!');
                }}
                className="icon-btn"
                title="Copy code"
              >
                <Copy size={16} />
              </button>
            </div>
          </div>

          <div className="instructions">
            <h3>Instructions:</h3>
            <ol>
              <li>Open your authenticator app (Google Authenticator, Microsoft Authenticator, Authy, etc.)</li>
              <li>Scan the QR code above or enter the manual code</li>
              <li>Enter the 6-digit code from your app below</li>
            </ol>
          </div>

          <form onSubmit={handleVerify} className="auth-form">
            <div className="auth-field">
              <label htmlFor="code" style={{ textAlign: 'center' }}>Enter 6-digit code</label>
              <OtpInput value={code} onChange={setCode} autoFocus error={failedAttempts > 0} />
              {failedAttempts > 0 && failedAttempts < 3 && (
                <p className="auth-error-text">{3 - failedAttempts} attempt(s) remaining</p>
              )}
            </div>
            <button type="submit" className="btn-primary auth-submit" disabled={verifying || code.length !== 6}>
              {verifying ? 'Verifying...' : 'Verify and Enable MFA'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MFASetup;
